import { WorkerConfigurationApiMode } from './worker-configuration.api';

/**
 * Workflow options that only support may configure.
 * Provider UI shows them read-only and must not write or default them.
 */
const SUPPORT_ONLY_WORKFLOW_OPTIONS: Readonly<Record<string, ReadonlySet<string>>> = {
    EXECUTE_ENTER_CODING: new Set(['codingClaimTags']),
    NEED_MEDICAL_RECORDS: new Set(['uploadDocuments']),
    NEED_PRIMARY_TRANSMIT: new Set(['analyzeClaim']),
    WAIT_FOR_PROFESSIONAL_CREATED: new Set(['waitForCoding']),
};

/**
 * Support-only options the provider endpoint rejects by presence, whatever the value.
 * They must be omitted from the provider request body, not echoed back unchanged.
 */
const PROVIDER_REJECTED_WORKFLOW_OPTIONS: Readonly<Record<string, ReadonlySet<string>>> = {
    NEED_MEDICAL_RECORDS: new Set(['uploadDocuments']),
    NEED_PRIMARY_TRANSMIT: new Set(['analyzeClaim']),
    WAIT_FOR_PROFESSIONAL_CREATED: new Set(['waitForCoding']),
};

/**
 * Options the options endpoint may still advertise but the UI no longer owns at this status.
 * Task grouping moved from the automation worker onto each rule (`AutomationRule.group`), so the
 * worker-level `group` must not be rendered, seeded from its default, or written back.
 */
const RETIRED_WORKFLOW_OPTIONS: Readonly<Record<string, ReadonlySet<string>>> = {
    PROVIDER_DENIAL_WORKER: new Set(['group']),
    PROVIDER_REJECTION_WORKER: new Set(['group']),
};

/** True when the option is retired at this status and must never reach the draft or the payload. */
export function isRetiredWorkflowOption(status: string, key: string): boolean {
    return RETIRED_WORKFLOW_OPTIONS[status]?.has(key) ?? false;
}

/** True when the option can only be edited from the support tool. */
export function isSupportOnlyWorkflowOption(status: string, key: string): boolean {
    return SUPPORT_ONLY_WORKFLOW_OPTIONS[status]?.has(key) ?? false;
}

/** True when the provider endpoint refuses a request body containing the option. */
export function isProviderRejectedWorkflowOption(status: string, key: string): boolean {
    return PROVIDER_REJECTED_WORKFLOW_OPTIONS[status]?.has(key) ?? false;
}

/** True when the current API mode may edit the option. */
export function canEditWorkflowOption(
    apiMode: WorkerConfigurationApiMode,
    status: string,
    key: string,
): boolean {
    if (apiMode === 'support') {
        return true;
    }

    return !isSupportOnlyWorkflowOption(status, key);
}
